import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "../../features/auth/hooks/useAuth";
import { resetSystemData } from "../../admin/adminService";
import apiFetch from "../services/apiClient";

function useCondominioInfo() {
  const [info, setInfo] = useState({ nombre: "Cargando…", logo_url: null });
  useEffect(() => {
    apiFetch("/api/residents/condominio/")
      .then(r => r.json())
      .then(data => {
        const condo = data.results?.[0] ?? data;
        if (condo?.nombre) setInfo({ nombre: condo.nombre, logo_url: condo.logo_url ?? null });
      })
      .catch(() => {});
  }, []);
  return info;
}

// ── Nav item components ───────────────────────────────────────────────────────
function NavLink({ to, icon, children, location }) {
  const active = location.pathname === to && !location.search;
  return (
    <li>
      <Link to={to} style={{ textDecoration: "none" }}>
        <div className={`sb-item ${active ? "sb-item--active" : ""}`}>
          <i className={`fa-solid ${icon} sb-icon`}/>
          <span className="sb-label sidebar__hide">{children}</span>
          {active && <span className="sb-pip"/>}
        </div>
      </Link>
    </li>
  );
}

function NavGroup({ icon, label, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  // Keep toggle logic compatible with original toggleSubMenu behaviour
  function toggle(e) {
    const layout  = document.querySelector(".contentDashboard");
    const sidebar = document.querySelector(".contentDashboard > aside");
    const isCollapsed = layout?.classList.contains("collapsed");
    const isMobile    = window.matchMedia("(max-width: 720px)").matches;
    if (isCollapsed && !isMobile) {
      // Expand sidebar first, DON'T open/close the group
      layout.classList.remove("collapsed");
      sidebar?.classList.remove("collapsed");
      return;
    }
    setOpen(v => !v);
  }

  return (
    <li>
      <button onClick={toggle}
        className={`sb-group-btn ${open ? "sb-group-btn--open" : ""}`}
        aria-expanded={open}>
        <div className="sb-group-left">
          <i className={`fa-solid ${icon} sb-icon`}/>
          <span className="sb-label sidebar__hide">{label}</span>
        </div>
        <i className="fa-solid fa-chevron-right sb-chevron sidebar__hide"/>
      </button>
      <div className={`sb-group-body ${open ? "sb-group-body--open" : ""}`}>
        <ul className="sb-children">
          {children}
        </ul>
      </div>
    </li>
  );
}

function NavChild({ to, icon, children, location, indent = 1 }) {
  const [path, qs] = to.split("?");
  const active =
    // Exact pathname match (no partials)
    (location.pathname === path && !qs) ||
    // Tab match for ?tab= links — must match both path AND tab param exactly
    (!!qs && location.pathname === path &&
      new URLSearchParams(location.search).get("tab") === new URLSearchParams("?" + qs).get("tab"));
  return (
    <li>
      <Link to={to} style={{ textDecoration: "none" }}>
        <div className={`sb-child sb-child--l${indent} ${active ? "sb-child--active" : ""}`}>
          {icon && <i className={`fa-solid ${icon} sb-child-icon`}/>}
          <span className="sidebar__hide">{children}</span>
        </div>
      </Link>
    </li>
  );
}

// ── Main Sidebar ──────────────────────────────────────────────────────────────
export default function Sidebar({ toggleSidebar }) {
  const { logout } = useAuth();
  const condo      = useCondominioInfo();
  const location   = useLocation();
  const isSuperUser = localStorage.getItem("is_superuser") === "true";

  // Keep original closeAllSubMenus on window for compatibility
  function closeAllSubMenus() {}
  window.closeAllSubMenus = closeAllSubMenus;

  function handleLogout() { logout(); }

  async function handleReset() {
    if (!window.confirm("⚠️ Esto borrará TODOS los datos. ¿Estás seguro?")) return;
    try {
      await resetSystemData();
      console.info("Datos eliminados correctamente");
      window.location.reload();
    } catch { console.error("Error al eliminar datos"); }
  }

  const isEstados  = location.pathname === "/finanzas/estados";
  const currentTab = new URLSearchParams(location.search).get("tab");

  const TABS = [
    { label: "Balance de Comprobación", tab: "balance-comprobacion", icon: "fa-scale-balanced"   },
    { label: "Estado de Resultados",    tab: "estado-resultados",    icon: "fa-chart-line"       },
    { label: "Balance General",         tab: "balance-general",      icon: "fa-building-columns" },
    { label: "Estado Financiero (PDF)", tab: "estado-financiero",    icon: "fa-file-pdf"         },
  ];

  return (
    <aside className="sb-root">

      {/* ── Fuentes ── */}
      <link rel="preconnect" href="https://fonts.googleapis.com"/>
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous"/>
      <link href="https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet"/>
      <link href="https://fonts.cdnfonts.com/css/satoshi" rel="stylesheet"/>

      {/* ── Condominio ── */}
      <div className="sb-brand">
        {condo.logo_url ? (
          <img src={condo.logo_url} alt="Logo"
            style={{ width:38, height:38, borderRadius:9, objectFit:"cover", flexShrink:0 }}/>
        ) : (
          <div className="sb-brand-icon">
            <i className="fa-solid fa-building"/>
          </div>
        )}
        <div className="sb-brand-text sidebar__hide">
          <span className="sb-brand-name">{condo.nombre}</span>
          <span className="sb-brand-role">Administrador</span>
        </div>
      </div>

      {/* ── Nav ── */}
      <nav className="sb-nav hide-scrollbar">

        {/* Dashboard */}
        <div className="sb-section-label sidebar__hide">Principal</div>
        <ul className="sb-list">
          <NavLink to="/" icon="fa-house" location={location}>Dashboard</NavLink>
          <NavLink to="/residentes" icon="fa-users" location={location}>Residentes</NavLink>
          {isSuperUser && (
            <NavLink to="/admin" icon="fa-shield-halved" location={location}>Panel Admin</NavLink>
          )}
        </ul>

        {/* Finanzas */}
        <div className="sb-section-label sidebar__hide">Finanzas</div>
        <ul className="sb-list">

          <NavGroup icon="fa-chart-line" label="General">
            <NavChild to="/finanzas/asientos"  icon="fa-book-open" location={location}>Asientos de Diario</NavChild>
            <NavChild to="/finanzas/catalogo"  icon="fa-sitemap" location={location}>Catálogo de Cuentas</NavChild>
            <NavChild to="/finanzas/ingresos"  icon="fa-arrow-trend-up" location={location}>Registro de Ingresos</NavChild>
            <NavChild to="/finanzas/gastos"    icon="fa-arrow-trend-down" location={location}>Registro de Gastos</NavChild>
          </NavGroup>

          <NavGroup icon="fa-file-invoice" label="Cuentas por Cobrar">
            <NavChild to="/finanzas/cxc"      icon="fa-gauge" location={location}>Dashboard</NavChild>
            <NavChild to="/finanzas/facturas" icon="fa-file-invoice" location={location}>Facturas</NavChild>
            <NavChild to="/finanzas/recibos"  icon="fa-receipt" location={location}>Recibos</NavChild>
          </NavGroup>

          <NavGroup icon="fa-file-invoice-dollar" label="Cuentas por Pagar">
            <NavChild to="/finanzas/cxp"          icon="fa-gauge" location={location}>Dashboard</NavChild>
            <NavChild to="/finanzas/cxp/facturas" icon="fa-file-invoice-dollar" location={location}>Facturas</NavChild>
            <NavChild to="/finanzas/cxp/recibos"  icon="fa-receipt" location={location}>Recibos</NavChild>
          </NavGroup>

          <NavGroup icon="fa-chart-bar" label="Estados Financieros"
            defaultOpen={isEstados}>
            {TABS.map(({ label, tab, icon }) => (
              <NavChild key={tab}
                to={`/finanzas/estados?tab=${tab}`}
                icon={icon} location={location}>
                {label}
              </NavChild>
            ))}
          </NavGroup>

        </ul>

      </nav>

      {/* ── Footer ── */}
      <div className="sb-footer">
        <button className="sb-reset-btn sidebar__hide" onClick={handleReset}>
          Resetear Sistema
        </button>
        <button id="btnToggleSidebarCollapsed"
          className="sb-collapse-btn"
          aria-label="Toggle sidebar" aria-pressed="false"
          onClick={toggleSidebar}>
          <i className="fa-solid fa-bars"/>
        </button>
      </div>

    </aside>
  );
}
